/**
 * Client for server-side embedding job queue (Featherless via Vercel API).
 */

import { authHeaders as resolveAuthHeaders } from '@/lib/authToken';

export type EmbeddingJobType = 'document' | 'thread' | 'repo' | 'integration' | 'topic' | 'space';

export interface EmbeddingJobRecord {
  id: string;
  job_type: EmbeddingJobType;
  target_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  progress?: Record<string, unknown>;
  last_error?: string | null;
  attempts?: number;
  max_attempts?: number;
  created_at?: string;
  completed_at?: string | null;
  updated_at?: string | null;
  symbols_status?: 'pending' | 'processing' | 'completed' | 'failed' | 'skipped' | null;
  indexCompleteness?: RepoIndexCompleteness;
}

/** Jobs the worker can still claim or is already running. Exhausted pending rows are not runnable. */
export function isRunnableEmbeddingJob(job: EmbeddingJobRecord): boolean {
  const attempts = job.attempts ?? 0;
  const maxAttempts = job.max_attempts ?? 3;
  if (job.status === 'processing') return true;
  return job.status === 'pending' && attempts < maxAttempts;
}

export interface RepoJobProgressView {
  status: 'indexing' | 'done' | 'error';
  progress: number;
  batchIndex?: number;
  totalBatches?: number;
  chunksIndexed?: number;
  errorMessage?: string;
  phase?: string;
  lastHeartbeat?: string;
  indexCompleteness?: 'none' | 'pass1Only' | 'partial' | 'complete' | 'unknown';
  symbolsStatus?: EmbeddingJobRecord['symbols_status'];
}

export type RepoIndexCompleteness = NonNullable<RepoJobProgressView['indexCompleteness']>;

function isRecoverableRepoJobError(job: EmbeddingJobRecord): boolean {
  const msg = job.last_error ?? '';
  return /ENOTFOUND|ECONNRESET|ETIMEDOUT|fetch failed|network|socket hang up|EAI_AGAIN|ECONNREFUSED/i.test(
    msg
  );
}

function isExhaustedRepoJob(job: EmbeddingJobRecord): boolean {
  const attempts = job.attempts ?? 0;
  const maxAttempts = job.max_attempts ?? 3;
  if (job.status === 'failed') return attempts >= maxAttempts;
  return job.status === 'pending' && attempts >= maxAttempts;
}

function findRepoJobForTarget(
  jobs: EmbeddingJobRecord[],
  targetId: string
): EmbeddingJobRecord | undefined {
  const matching = jobs.filter(
    (j) => j.job_type === 'repo' && j.target_id === targetId
  );
  if (matching.length === 0) return undefined;

  const rank = (job: EmbeddingJobRecord): number => {
    if (job.status === 'processing') return 4;
    if (job.status === 'pending') return 3;
    if (job.status === 'failed' || job.status === 'completed') return 2;
    return 1;
  };

  return [...matching].sort((a, b) => {
    const rankDiff = rank(b) - rank(a);
    if (rankDiff !== 0) return rankDiff;
    const aTime = new Date(a.updated_at ?? a.created_at ?? 0).getTime();
    const bTime = new Date(b.updated_at ?? b.created_at ?? 0).getTime();
    return bTime - aTime;
  })[0];
}

export function deriveRepoIndexCompleteness(
  job: EmbeddingJobRecord
): RepoIndexCompleteness {
  if (job.status === 'completed') return 'complete';
  const p = job.progress ?? {};
  if (p.phase === 'done') return 'complete';
  const embedBatch = typeof p.embedBatchIndex === 'number' ? p.embedBatchIndex : 0;
  if (embedBatch > 0) return 'partial';
  if (p.pass1Done) return 'pass1Only';
  return 'none';
}

export function deriveLiveRepoIndexStatus(
  view: RepoJobProgressView
): 'none' | 'pass1Only' | 'partial' | 'complete' | undefined {
  if (view.status === 'done') return 'complete';
  if (view.status === 'error') return undefined;
  if (view.indexCompleteness === 'partial' || view.phase === 'embed' || (view.progress ?? 0) > 15) {
    return 'partial';
  }
  if (view.indexCompleteness === 'pass1Only' || view.phase === 'pass1') {
    return 'pass1Only';
  }
  if (
    view.indexCompleteness === 'none' ||
    view.phase === 'preflight' ||
    view.phase === 'download' ||
    view.phase === 'chunk' ||
    view.phase === 'symbols'
  ) {
    return 'none';
  }
  if (view.indexCompleteness && view.indexCompleteness !== 'unknown') {
    return view.indexCompleteness;
  }
  return undefined;
}

const API_BASE = '';
const FRESH_PROCESSING_MS = 3 * 60 * 1000;
const REPO_STALE_HEARTBEAT_MS = 60_000;
const FRESH_EMBED_HEARTBEAT_MS = 60_000;
const PROCESS_FETCH_TIMEOUT_MS = 90_000;

/** Dedupe /process for the same checkpoint wave while a dispatch is in flight. */
const lastDispatchedRepoCheckpoint = new Map<
  string,
  { batchIndex: number; at: number }
>();

let processInFlight: Promise<{ processed: number; jobIds: string[]; errors: string[] }> | null =
  null;

async function authHeaders(): Promise<HeadersInit> {
  return resolveAuthHeaders();
}

export function mapRepoJobProgress(job: EmbeddingJobRecord): RepoJobProgressView {
  const p = job.progress ?? {};
  const lastHeartbeat =
    typeof p.lastHeartbeat === 'string'
      ? p.lastHeartbeat
      : job.updated_at ?? undefined;

  const attempts = job.attempts ?? 0;
  const maxAttempts = job.max_attempts ?? 3;
  const exhausted = isExhaustedRepoJob(job);
  const partialProgress = typeof p.percent === 'number' ? p.percent : 0;
  const hasPartialProgress =
    partialProgress > 0 ||
    (typeof p.embedBatchIndex === 'number' && p.embedBatchIndex > 0);

  if (job.status === 'failed' || exhausted) {
    if (
      (exhausted || isRecoverableRepoJobError(job)) &&
      (hasPartialProgress || exhausted)
    ) {
      return {
        status: 'indexing',
        progress: partialProgress,
        batchIndex: typeof p.batchIndex === 'number' ? p.batchIndex : undefined,
        totalBatches: typeof p.totalBatches === 'number' ? p.totalBatches : undefined,
        chunksIndexed: typeof p.chunksIndexed === 'number' ? p.chunksIndexed : undefined,
        phase: typeof p.phase === 'string' ? p.phase : undefined,
        lastHeartbeat,
        indexCompleteness: deriveRepoIndexCompleteness(job),
      };
    }

    return {
      status: 'error',
      progress: partialProgress,
      errorMessage: exhausted
        ? `Indexing exhausted ${maxAttempts} attempts — retrying…`
        : (job.last_error ?? 'Indexing failed'),
      phase: typeof p.phase === 'string' ? p.phase : undefined,
      lastHeartbeat,
    };
  }
  if (job.status === 'completed') {
    return {
      status: 'done',
      progress: 100,
      batchIndex: typeof p.batchIndex === 'number' ? p.batchIndex : undefined,
      totalBatches: typeof p.totalBatches === 'number' ? p.totalBatches : undefined,
      chunksIndexed: typeof p.chunksIndexed === 'number' ? p.chunksIndexed : undefined,
      phase: typeof p.phase === 'string' ? p.phase : 'done',
      lastHeartbeat,
      symbolsStatus: job.symbols_status ?? undefined,
    };
  }
  return {
    status: 'indexing',
    progress: typeof p.percent === 'number' ? p.percent : 0,
    batchIndex: typeof p.batchIndex === 'number' ? p.batchIndex : undefined,
    totalBatches: typeof p.totalBatches === 'number' ? p.totalBatches : undefined,
    chunksIndexed: typeof p.chunksIndexed === 'number' ? p.chunksIndexed : undefined,
    phase: typeof p.phase === 'string' ? p.phase : undefined,
    lastHeartbeat,
    indexCompleteness: deriveRepoIndexCompleteness(job),
  };
}

async function parseError(res: Response): Promise<string> {
  const fallback = `Embeddings API failed (${res.status})`;
  try {
    const text = await res.text();
    try {
      const json = JSON.parse(text) as { error?: string; message?: string };
      const msg = json.error || json.message;
      if (typeof msg === 'string' && msg.trim()) return msg;
    } catch {
      // not JSON
    }
    const snippet = text.trim().slice(0, 200);
    return snippet || res.statusText || fallback;
  } catch {
    return res.statusText || fallback;
  }
}

function pollIntervalMs(pendingCount: number, basePollMs: number): number {
  if (pendingCount > 30) return Math.max(basePollMs, 5000);
  if (pendingCount > 10) return Math.max(basePollMs, 3000);
  return basePollMs;
}

export function hasFreshProcessingJob(jobs: EmbeddingJobRecord[]): boolean {
  const now = Date.now();
  return jobs.some((job) => {
    if (job.status !== 'processing') return false;
    if (job.job_type === 'repo') return !shouldProcessRepoJob(job);
    if (!job.updated_at) return true;
    return now - new Date(job.updated_at).getTime() < FRESH_PROCESSING_MS;
  });
}

export function shouldProcessRepoJob(job: EmbeddingJobRecord): boolean {
  if (job.status === 'pending') {
    const embedBatch =
      typeof job.progress?.embedBatchIndex === 'number'
        ? job.progress.embedBatchIndex
        : 0;
    if (embedBatch > 0) {
      const last = lastDispatchedRepoCheckpoint.get(job.id);
      const now = Date.now();
      if (
        last &&
        last.batchIndex === embedBatch &&
        now - last.at < PROCESS_FETCH_TIMEOUT_MS
      ) {
        return false;
      }
      lastDispatchedRepoCheckpoint.set(job.id, { batchIndex: embedBatch, at: now });
    }
    return true;
  }
  if (job.status !== 'processing') return false;

  const p = job.progress ?? {};
  const phase = typeof p.phase === 'string' ? p.phase : undefined;
  if (phase === 'embed') {
    const heartbeat =
      typeof p.lastHeartbeat === 'string' ? p.lastHeartbeat : job.updated_at;
    if (
      heartbeat &&
      Date.now() - new Date(heartbeat).getTime() < FRESH_EMBED_HEARTBEAT_MS
    ) {
      return false;
    }
  }

  const heartbeat =
    typeof p.lastHeartbeat === 'string' ? p.lastHeartbeat : job.updated_at;
  if (!heartbeat) return true;
  return Date.now() - new Date(heartbeat).getTime() > REPO_STALE_HEARTBEAT_MS;
}

async function runProcessOnce(): Promise<{ processed: number; jobIds: string[]; errors: string[] }> {
  if (processInFlight) return processInFlight;

  processInFlight = (async () => {
    try {
      const res = await fetch(`${API_BASE}/api/embeddings/process`, {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({ limit: 1 }),
        signal: AbortSignal.timeout(PROCESS_FETCH_TIMEOUT_MS),
      });
      if (!res.ok && res.status !== 202) throw new Error(await parseError(res));
      const data = await res.json();
      return {
        processed: Number(data.processed) || Number(data.accepted) || 0,
        jobIds: (data.jobIds || []) as string[],
        errors: (data.errors || []) as string[],
      };
    } catch (err) {
      if (err instanceof DOMException && err.name === 'TimeoutError') {
        console.warn('[embeddingJobService] process request timed out — worker may still be running');
        return { processed: 0, jobIds: [], errors: ['process timeout'] };
      }
      throw err;
    }
  })();

  try {
    return await processInFlight;
  } finally {
    processInFlight = null;
  }
}

function resetProcessInFlight(): void {
  processInFlight = null;
}

export const embeddingJobService = {
  async enqueue(
    jobs: Array<{
      job_type: EmbeddingJobType;
      target_id: string;
      payload?: Record<string, unknown>;
      priority?: number;
    }>
  ): Promise<EmbeddingJobRecord[]> {
    const res = await fetch(`${API_BASE}/api/embeddings/enqueue`, {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify({ jobs }),
    });
    if (!res.ok) throw new Error(`Embedding enqueue failed: ${await parseError(res)}`);
    const data = await res.json();
    return (data.jobs || []) as EmbeddingJobRecord[];
  },

  async process(limit = 1): Promise<{ processed: number; jobIds: string[]; errors: string[] }> {
    void limit;
    return runProcessOnce();
  },

  resetProcessInFlight(): void {
    resetProcessInFlight();
  },

  async getStatus(jobIds?: string[]): Promise<EmbeddingJobRecord[]> {
    const headers = await authHeaders();

    if (!jobIds?.length) {
      const res = await fetch(`${API_BASE}/api/embeddings/status`, {
        method: 'GET',
        headers,
      });
      if (!res.ok) throw new Error(await parseError(res));
      const data = await res.json();
      return (data.jobs || []) as EmbeddingJobRecord[];
    }

    const chunkSize = 100;
    const allJobs: EmbeddingJobRecord[] = [];

    for (let i = 0; i < jobIds.length; i += chunkSize) {
      const chunk = jobIds.slice(i, i + chunkSize);
      const usePost = chunk.length > 20;
      const res = usePost
        ? await fetch(`${API_BASE}/api/embeddings/status`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ jobIds: chunk }),
          })
        : await fetch(
            `${API_BASE}/api/embeddings/status?jobIds=${encodeURIComponent(chunk.join(','))}`,
            { method: 'GET', headers }
          );
      if (!res.ok) throw new Error(await parseError(res));
      const data = await res.json();
      allJobs.push(...((data.jobs || []) as EmbeddingJobRecord[]));
    }

    return allJobs;
  },

  async recoverStale(staleMinutes = 10): Promise<{ recovered: number; slotsRecovered: number }> {
    const res = await fetch(`${API_BASE}/api/embeddings/recover`, {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify({ staleMinutes }),
    });
    if (!res.ok) throw new Error(await parseError(res));
    const data = await res.json();
    return {
      recovered: Number(data.recovered) || 0,
      slotsRecovered: Number(data.slotsRecovered) || 0,
    };
  },

  async triggerSymbolsBackground(): Promise<void> {
    try {
      await fetch(`${API_BASE}/api/embeddings/symbols`, {
        method: 'POST',
        headers: await authHeaders(),
        signal: AbortSignal.timeout(120_000),
      });
    } catch (err) {
      console.warn('[embeddingJobService] symbols background tick failed:', err);
    }
  },

  async retryChunkFailedJobs(): Promise<number> {
    const res = await fetch(`${API_BASE}/api/embeddings/retry-failed`, {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify({}),
    });
    if (!res.ok) throw new Error(await parseError(res));
    const data = await res.json();
    return Number(data.reset) || 0;
  },

  async backfillMissing(): Promise<{ queued: number; eligible: number }> {
    const res = await fetch(`${API_BASE}/api/embeddings?route=backfill-missing`, {
      method: 'POST',
      headers: await authHeaders(),
      body: '{}',
    });
    if (!res.ok) throw new Error(await parseError(res));
    const data = await res.json();
    return {
      queued: Number(data.queued) || 0,
      eligible: Number(data.eligible) || 0,
    };
  },

  /** Cancel all pending/processing/failed jobs for a fresh start. */
  async clearQueue(): Promise<{ cancelled: number; slotsRecovered: number }> {
    const res = await fetch(`${API_BASE}/api/embeddings/clear-queue`, {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify({}),
    });
    if (!res.ok) throw new Error(await parseError(res));
    const data = await res.json();
    return {
      cancelled: Number(data.cancelled) || 0,
      slotsRecovered: Number(data.slotsRecovered) || 0,
    };
  },

  /**
   * Process pending jobs until the queue is empty or timeout.
   */
  async drainQueue(
    options: {
      onProgress?: (job: EmbeddingJobRecord) => void;
      pollMs?: number;
      maxWaitMs?: number;
      forceRecover?: boolean;
    } = {}
  ): Promise<void> {
    const { pollMs = 3000, maxWaitMs, forceRecover = false } = options;
    await this.recoverStale(forceRecover ? 0 : 2);

    const jobs = await this.getStatus();
    const activeCount = jobs.filter(
      (j) => j.status === 'pending' || j.status === 'processing'
    ).length;
    const waitMs =
      maxWaitMs ??
      Math.min(3_600_000, Math.max(600_000, activeCount * 90_000));

    const deadline = Date.now() + waitMs;

    while (Date.now() < deadline) {
      await this.recoverStale(forceRecover ? 0 : 2).catch((err) => {
        console.warn('[embeddingJobService] recover during drain failed:', err);
      });

      const jobs = await this.getStatus();
      const active = jobs.filter(
        (j) => j.status === 'pending' || j.status === 'processing'
      );
      if (active.length === 0) return;

      for (const job of active) {
        options.onProgress?.(job);
      }

      if (!hasFreshProcessingJob(active)) {
        await runProcessOnce().catch((err) => {
          console.warn('[embeddingJobService] drain process tick failed:', err);
        });
      }

      await new Promise((r) =>
        setTimeout(r, pollIntervalMs(active.length, pollMs))
      );
    }

    throw new Error('Embedding queue drain timed out');
  },

  /**
   * Enqueue jobs, then poll worker until all complete or fail.
   */
  async enqueueAndWait(
    jobs: Array<{ job_type: EmbeddingJobType; target_id: string; payload?: Record<string, unknown> }>,
    onProgress?: (job: EmbeddingJobRecord) => void,
    options: { pollMs?: number; maxWaitMs?: number } = {}
  ): Promise<void> {
    const maxWaitMs =
      options.maxWaitMs ??
      Math.min(3_600_000, Math.max(600_000, jobs.length * 90_000));
    const basePollMs = options.pollMs ?? 2000;

    await this.recoverStale(2).catch((err) => {
      console.warn('[embeddingJobService] recover before enqueue failed:', err);
    });

    const enqueued = await this.enqueue(jobs);
    const ids = enqueued.map((j) => j.id).filter(Boolean);
    // Returning quietly here reported "done" for work that was never queued —
    // callers then recorded every item as indexed. Fail loudly instead, like
    // the failed-job and timeout paths below.
    if (ids.length === 0) {
      throw new Error(
        `Embedding enqueue returned no jobs for ${jobs.length} item(s) — nothing was queued`
      );
    }

    const deadline = Date.now() + maxWaitMs;
    let pending = new Set(ids);

    while (pending.size > 0 && Date.now() < deadline) {
      const statuses = await this.getStatus([...pending]);

      if (!hasFreshProcessingJob(statuses)) {
        await runProcessOnce().catch((err) => {
          console.warn('[embeddingJobService] process tick failed:', err);
        });
      }

      for (const job of statuses) {
        if (onProgress) onProgress(job);
        if (job.status === 'completed' || job.status === 'failed' || job.status === 'cancelled') {
          pending.delete(job.id);
          if (job.status === 'failed') {
            throw new Error(
              `Embedding job failed (${job.target_id}): ${job.last_error || job.id}`
            );
          }
        }
      }

      if (pending.size > 0) {
        await new Promise((r) =>
          setTimeout(r, pollIntervalMs(pending.size, basePollMs))
        );
      }
    }

    if (pending.size > 0) {
      throw new Error('Embedding jobs timed out');
    }
  },

  async enqueueRepoIndex(
    owner: string,
    repo: string,
    options: { forceReindex?: boolean; forceFullReindex?: boolean; ref?: string } = {}
  ): Promise<EmbeddingJobRecord[]> {
    const forceFullReindex = options.forceFullReindex ?? options.forceReindex ?? false;
    return this.enqueue([
      {
        job_type: 'repo',
        target_id: `${owner}/${repo}`,
        payload: {
          owner,
          repo,
          ref: options.ref,
          forceFullReindex,
          forceReindex: forceFullReindex,
        },
        priority: forceFullReindex ? 1 : 0,
      },
    ]);
  },

  async waitForRepoJob(
    targetId: string,
    onProgress?: (view: RepoJobProgressView, job: EmbeddingJobRecord) => void,
    options: { pollMs?: number; maxWaitMs?: number; signal?: AbortSignal } = {}
  ): Promise<void> {
    const maxWaitMs = options.maxWaitMs ?? 3_600_000;
    const basePollMs = options.pollMs ?? 2000;
    const deadline = Date.now() + maxWaitMs;
    const signal = options.signal;

    if (signal?.aborted) {
      throw new DOMException('Repo indexing aborted', 'AbortError');
    }

    await this.recoverStale(2).catch((err) => {
      console.warn('[embeddingJobService] recover before repo wait failed:', err);
    });

    let lastExhaustedResetAt = 0;

    while (Date.now() < deadline) {
      if (signal?.aborted) {
        throw new DOMException('Repo indexing aborted', 'AbortError');
      }

      await this.recoverStale(1).catch((err) => {
        console.warn('[embeddingJobService] recover during repo wait failed:', err);
      });

      const jobs = await this.getStatus();
      const job = findRepoJobForTarget(jobs, targetId);

      if (!job) {
        await new Promise((r) => setTimeout(r, basePollMs));
        continue;
      }

      const view = mapRepoJobProgress(job);
      onProgress?.(view, job);

      if (job.status === 'completed') {
        void this.triggerSymbolsBackground();
        return;
      }
      if (job.status === 'failed' && !isRecoverableRepoJobError(job)) {
        throw new Error(job.last_error || `Repo indexing failed for ${targetId}`);
      }

      const attempts = job.attempts ?? 0;
      const maxAttempts = job.max_attempts ?? 3;
      const needsRecovery =
        isExhaustedRepoJob(job) ||
        (job.status === 'failed' && isRecoverableRepoJobError(job));

      const embedBatch =
        typeof job.progress?.embedBatchIndex === 'number'
          ? job.progress.embedBatchIndex
          : 0;
      const isCheckpointResume =
        embedBatch > 0 &&
        job.status === 'pending' &&
        !isExhaustedRepoJob(job);

      if (needsRecovery && !isCheckpointResume) {
        const now = Date.now();
        if (now - lastExhaustedResetAt > 5000) {
          lastExhaustedResetAt = now;
          const slashIdx = targetId.indexOf('/');
          if (slashIdx > 0) {
            await this.enqueueRepoIndex(
              targetId.slice(0, slashIdx),
              targetId.slice(slashIdx + 1),
              {}
            ).catch((err) => {
              console.warn('[embeddingJobService] auto-reset exhausted repo job failed:', err);
            });
          }
        }
        await new Promise((r) => setTimeout(r, pollIntervalMs(1, basePollMs)));
        continue;
      }

      if (job.status === 'failed') {
        throw new Error(job.last_error || `Repo indexing failed for ${targetId}`);
      }

      if (shouldProcessRepoJob(job)) {
        await runProcessOnce().catch((err) => {
          console.warn('[embeddingJobService] repo process tick failed:', err);
        });
      }

      await new Promise((r) => setTimeout(r, pollIntervalMs(1, basePollMs)));
    }

    throw new Error(`Repo indexing timed out for ${targetId}`);
  },

  async waitForSymbolsComplete(
    targetId: string,
    onProgress?: (
      symbolsStatus: EmbeddingJobRecord['symbols_status'],
      job: EmbeddingJobRecord
    ) => void,
    options: { pollMs?: number; maxWaitMs?: number; signal?: AbortSignal } = {}
  ): Promise<NonNullable<EmbeddingJobRecord['symbols_status']>> {
    const maxWaitMs = options.maxWaitMs ?? 3_600_000;
    const basePollMs = options.pollMs ?? 2500;
    const deadline = Date.now() + maxWaitMs;
    const signal = options.signal;

    if (signal?.aborted) {
      throw new DOMException('Symbols indexing aborted', 'AbortError');
    }

    void this.triggerSymbolsBackground();

    while (Date.now() < deadline) {
      if (signal?.aborted) {
        throw new DOMException('Symbols indexing aborted', 'AbortError');
      }

      const jobs = await this.getStatus();
      const job = findRepoJobForTarget(jobs, targetId);

      if (!job || job.status !== 'completed') {
        await new Promise((r) => setTimeout(r, basePollMs));
        continue;
      }

      const symbolsStatus = job.symbols_status ?? null;
      onProgress?.(symbolsStatus, job);

      if (
        symbolsStatus === 'completed' ||
        symbolsStatus === 'failed' ||
        symbolsStatus === 'skipped'
      ) {
        return symbolsStatus;
      }

      void this.triggerSymbolsBackground();
      await new Promise((r) => setTimeout(r, basePollMs));
    }

    throw new Error(`Symbols indexing timed out for ${targetId}`);
  },
};
